import React, { useState } from "react";
import { Row, Col, Form, InputGroup, Alert } from "react-bootstrap";
import { IconUser, IconMail, IconPhone, IconId } from "@tabler/icons-react";
import { Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import NumberFormat from "react-number-format";

const CustomerProfileStep = ({ form, isViewMode, isEditMode, loading }) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = form;
  const [profileType, setProfileType] = useState("PF"); // Default to Pessoa Física

  // Watch the cpf_cnpj field for validation
  const cpfCnpjValue = watch("cpf_cnpj") || "";

  // Handle profile type change
  const handleProfileTypeChange = (e) => {
    const newProfileType = e.target.value;
    setProfileType(newProfileType);
    // Clear cpf_cnpj when switching profile type to avoid format conflicts
    setValue("cpf_cnpj", "");
    // Clear name/razao_social when switching
    setValue("name", "");
  };

  return (
    <div className="customer-profile-step">
      {isEditMode && (
        <Alert variant="info" className="mb-3">
          <strong>Editando dados do cliente:</strong> As alterações serão
          aplicadas ao perfil do cliente.
        </Alert>
      )}

      <Form.Group className="mb-3">
        <Form.Label>
          <strong>Tipo de Perfil *</strong>
        </Form.Label>
        <div>
          <Form.Check
            inline
            type="radio"
            label="Pessoa Física"
            value="PF"
            checked={profileType === "PF"}
            onChange={handleProfileTypeChange}
            disabled={isViewMode || loading}
            id="profile-type-pf"
          />
          <Form.Check
            inline
            type="radio"
            label="Pessoa Jurídica"
            value="PJ"
            checked={profileType === "PJ"}
            onChange={handleProfileTypeChange}
            disabled={isViewMode || loading}
            id="profile-type-pj"
          />
        </div>
      </Form.Group>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Label>
            <IconUser size={16} className="me-1" />
            <strong>
              {profileType === "PF" ? "Nome Completo" : "Razão Social"} *
            </strong>
          </Form.Label>
          <Controller
            name="name"
            control={control}
            rules={{
              required:
                profileType === "PF"
                  ? "Nome é obrigatório"
                  : "Razão Social é obrigatória",
              minLength: {
                value: 2,
                message: "Deve ter pelo menos 2 caracteres",
              },
            }}
            render={({ field }) => (
              <Form.Control
                {...field}
                type="text"
                placeholder={
                  profileType === "PF"
                    ? "Digite o nome completo"
                    : "Digite a razão social"
                }
                disabled={isViewMode || loading}
                isInvalid={!!errors.name}
              />
            )}
          />
          {errors.name && (
            <Form.Control.Feedback type="invalid">
              {errors.name.message}
            </Form.Control.Feedback>
          )}
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>
            <IconMail size={16} className="me-1" />
            <strong>Email *</strong>
          </Form.Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email é obrigatório",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Email deve ser válido",
              },
            }}
            render={({ field }) => (
              <Form.Control
                {...field}
                type="email"
                placeholder="exemplo@email.com"
                disabled={isViewMode || loading}
                isInvalid={!!errors.email}
              />
            )}
          />
          {errors.email && (
            <Form.Control.Feedback type="invalid">
              {errors.email.message}
            </Form.Control.Feedback>
          )}
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>
            <IconId size={16} className="me-1" />
            <strong>{profileType === "PF" ? "CPF" : "CNPJ"} *</strong>
          </Form.Label>
          <Controller
            name="cpf_cnpj"
            control={control}
            rules={{
              required:
                profileType === "PF"
                  ? "CPF é obrigatório"
                  : "CNPJ é obrigatório",
              validate: (value) => {
                const clean = value.replace(/\D/g, "");
                if (profileType === "PF") {
                  if (clean.length !== 11) {
                    return "CPF deve ter 11 dígitos";
                  }
                  if (/^(\d)\1+$/.test(clean)) {
                    return "CPF inválido";
                  }
                  return true;
                } else {
                  if (clean.length !== 14) {
                    return "CNPJ deve ter 14 dígitos";
                  }
                  if (/^(\d)\1+$/.test(clean)) {
                    return "CNPJ inválido";
                  }
                  return true;
                }
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <NumberFormat
                {...field}
                format={
                  profileType === "PF" ? "###.###.###-##" : "##.###.###/####-##"
                }
                mask="_"
                value={value}
                onValueChange={(values) => {
                  onChange(values.value);
                }}
                disabled={isViewMode || loading}
                placeholder={
                  profileType === "PF" ? "000.000.000-00" : "00.000.000/0000-00"
                }
                customInput={Form.Control}
                isInvalid={!!errors.cpf_cnpj}
              />
            )}
          />
          {errors.cpf_cnpj && (
            <Form.Control.Feedback type="invalid">
              {errors.cpf_cnpj.message}
            </Form.Control.Feedback>
          )}
          <Form.Text className="text-muted">
            {profileType === "PF"
              ? "Formato: 000.000.000-00"
              : "Formato: 00.000.000/0000-00"}
          </Form.Text>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Label>
            <IconPhone size={16} className="me-1" />
            <strong>Telefone</strong>
          </Form.Label>
          <Controller
            name="phone"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true; // Campo opcional
                const clean = value.replace(/\D/g, "");
                if (clean.length < 10 || clean.length > 11) {
                  return "Telefone deve ter 10 ou 11 dígitos";
                }
                return true;
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <InputMask
                {...field}
                mask="(99) 99999-9999"
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                disabled={isViewMode || loading}
              >
                {(inputProps) => (
                  <Form.Control
                    {...inputProps}
                    type="text"
                    placeholder="(11) 99999-9999"
                    isInvalid={!!errors.phone}
                  />
                )}
              </InputMask>
            )}
          />
          {errors.phone && (
            <Form.Control.Feedback type="invalid">
              {errors.phone.message}
            </Form.Control.Feedback>
          )}
          <Form.Text className="text-muted">Formato: (11) 99999-9999</Form.Text>
        </Col>
      </Row>

      {!isViewMode && (
        <Alert variant="primary" className="mt-3">
          <strong>Próximo passo:</strong> Após salvar os dados do cliente, você
          configurará o perfil de faturamento.
        </Alert>
      )}
    </div>
  );
};

export default CustomerProfileStep;
